const API_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.foundifai.com";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function fetchApi(endpoint: string, options: RequestInit = {}) {
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: "Request failed" }));
    throw new ApiError(res.status, error.error || "Request failed");
  }

  return res.json();
}

// Export as apiClient for backward compatibility
export const apiClient = async (endpoint: string, options: RequestInit = {}) => {
  // Don't set Content-Type for FormData - let browser set it with boundary
  const isFormData = options.body instanceof FormData;
  
  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: isFormData ? {
      ...options.headers,
    } : {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

export const api = {
  auth: {
    login: (email: string, password: string) =>
      fetchApi("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    logout: () =>
      fetchApi("/auth/logout", {
        method: "POST",
      }),
    me: () => fetchApi("/auth/me"),
  },
  admin: {
    dashboard: () => fetchApi("/admin/dashboard"),
    ventureRequests: {
      list: (params?: { status?: string; page?: number; limit?: number }) => {
        const query = new URLSearchParams();
        if (params?.status) query.set("status", params.status);
        if (params?.page) query.set("page", params.page.toString());
        if (params?.limit) query.set("limit", params.limit.toString());
        return fetchApi(`/admin/venture-requests?${query}`);
      },
      get: (id: string) => fetchApi(`/admin/venture-requests/${id}`),
      update: (id: string, data: { status?: string; notes?: string }) =>
        fetchApi(`/admin/venture-requests/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
    },
    investors: {
      list: (params?: { page?: number; limit?: number }) => {
        const query = new URLSearchParams();
        if (params?.page) query.set("page", params.page.toString());
        if (params?.limit) query.set("limit", params.limit.toString());
        return fetchApi(`/admin/investors?${query}`);
      },
      create: (data: {
        full_name: string;
        email: string;
        phone: string;
        national_id: string;
        governorate: string;
        country: string;
      }) =>
        fetchApi("/admin/investors", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      get: (id: string) => fetchApi(`/admin/investors/${id}`),
      update: (id: string, data: { isActive?: boolean; approvedAt?: Date | null }) =>
        fetchApi(`/admin/investors/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      resendInvite: (id: string) =>
        fetchApi(`/admin/investors/${id}/resend-invite`, {
          method: "POST",
        }),
      previewUrl: (id: string) =>
        fetchApi(`/admin/investors/${id}/preview-url`, {
          method: "POST",
        }),
      deactivate: (id: string) =>
        fetchApi(`/admin/investors/${id}/deactivate`, {
          method: "PATCH",
        }),
    },
    rounds: {
      list: () => fetchApi("/admin/rounds"),
      get: (id: string) => fetchApi(`/admin/rounds/${id}`),
      getInvestorPortalReadiness: async (id: string) => {
        if (!id) throw new ApiError(400, "Missing round id");
        try {
          return await fetchApi(`/admin/rounds/${id}/investor-portal-readiness`);
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            return fetchApi(`/admin/rounds/${id}/investor-portal/readiness`);
          }
          throw error;
        }
      },
      setInvestorPortalActivation: async (id: string, activate = true) => {
        if (!id) throw new ApiError(400, "Missing round id");
        try {
          return await fetchApi(`/admin/rounds/${id}/investor-portal-activation`, {
            method: "PUT",
            body: JSON.stringify({ activate }),
          });
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            return fetchApi(`/admin/rounds/${id}/investor-portal/activation`, {
              method: "PUT",
              body: JSON.stringify({ activate }),
            });
          }
          throw error;
        }
      },
      create: (data: any) =>
        fetchApi("/admin/rounds", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: any) =>
        fetchApi(`/admin/rounds/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      getAllocations: (id: string) => fetchApi(`/admin/rounds/${id}/allocations`),
      updateAllocations: (id: string, data: any) =>
        fetchApi(`/admin/rounds/${id}/allocations`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      getScenarios: (id: string) => fetchApi(`/admin/rounds/${id}/scenarios`),
      updateScenarios: (id: string, data: any) =>
        fetchApi(`/admin/rounds/${id}/scenarios`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
    },
    actuals: {
      list: (params?: { roundId?: string; month?: string }) => {
        const query = new URLSearchParams();
        if (params?.roundId) query.set("roundId", params.roundId);
        if (params?.month) query.set("month", params.month);
        const suffix = query.toString() ? `?${query.toString()}` : "";
        return fetchApi(`/admin/actuals${suffix}`);
      },
      forecastContext: (params: { month: string; round_id?: string | null }) => {
        const query = new URLSearchParams();
        query.set("month", params.month);
        if (params.round_id) query.set("round_id", params.round_id);
        return fetchApi(`/admin/actuals/forecast-context?${query.toString()}`);
      },
      getByMonth: (month: string) => fetchApi(`/admin/actuals/${encodeURIComponent(month)}`),
      create: (data: {
        id?: string;
        round_id?: string | null;
        month: string;
        achieved_revenue_usd: number;
        actual_expenses_usd?: number | null;
        notes?: string | null;
        source?: string | null;
        locked?: boolean | null;
      }) =>
        fetchApi("/admin/actuals", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (
        id: string,
        data: {
          round_id?: string | null;
          month: string;
          achieved_revenue_usd: number;
          actual_expenses_usd?: number | null;
          notes?: string | null;
          source?: string | null;
          locked?: boolean | null;
        }
      ) =>
        fetchApi(`/admin/actuals/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        fetchApi(`/admin/actuals/${id}`, {
          method: "DELETE",
        }),
    },
    ticketTypes: {
      list: () => fetchApi("/admin/ticket-types"),
      get: (id: string) => fetchApi(`/admin/ticket-types/${id}`),
      create: (data: any) =>
        fetchApi("/admin/ticket-types", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: any) =>
        fetchApi(`/admin/ticket-types/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        fetchApi(`/admin/ticket-types/${id}`, {
          method: "DELETE",
        }),
    },
    investments: {
      list: (params?: { roundId?: string; status?: string }) => {
        const query = new URLSearchParams();
        if (params?.roundId) query.set("roundId", params.roundId);
        if (params?.status) query.set("status", params.status);
        return fetchApi(`/admin/investments?${query}`);
      },
    },
    purchases: {
      list: (params?: { status?: string; search?: string }) => {
        const query = new URLSearchParams();
        if (params?.status) query.set("status", params.status);
        if (params?.search) query.set("search", params.search);
        return fetchApi(`/admin/purchases?${query}`);
      },
      reservationFormData: () => fetchApi("/admin/purchases/reservation-form-data"),
      createReservation: (data: {
        investorUserId: string;
        roundId: string;
        ticketTypeId: string;
        quantity: number;
        reservedAt: string;
      }) =>
        fetchApi("/admin/purchases/reservations", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      certificates: () => fetchApi("/admin/purchases/certificates"),
      certificateDraftDownloadUrl: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}/draft-download-url`),
      certificateSignedDownloadUrl: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}/signed-download-url`),
      regenerateCertificateDraft: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}/regenerate-draft`, {
          method: "POST",
        }),
      signCertificate: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}/sign`, {
          method: "POST",
        }),
      reSignCertificate: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}/re-sign`, {
          method: "POST",
        }),
      uploadSignedCertificate: async (id: string, data: FormData) => {
        const res = await apiClient(`/admin/purchases/certificates/${id}/upload-signed`, {
          method: "POST",
          body: data,
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({ error: "Upload failed" }));
          throw new ApiError(res.status, error.error || error.message || "Upload failed");
        }
        return res.json();
      },
      deleteCertificate: (id: string) =>
        fetchApi(`/admin/purchases/certificates/${id}`, {
          method: "DELETE",
        }),
      receipts: (id: string) => fetchApi(`/admin/purchases/${id}/receipts`),
      receiptDownloadUrl: (id: string) => fetchApi(`/admin/purchases/${id}/receipt-download-url`),
      purchaseReceiptDownloadUrl: (purchaseId: string, receiptId: string) =>
        fetchApi(`/admin/purchases/${purchaseId}/receipts/${receiptId}/download-url`),
      uploadReceipts: async (purchaseId: string, data: FormData) => {
        const res = await apiClient(`/admin/purchases/${purchaseId}/receipts`, {
          method: "POST",
          body: data,
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({ error: "Upload failed" }));
          throw new ApiError(res.status, error.error || error.message || "Upload failed");
        }
        return res.json();
      },
      deletePurchase: (id: string) =>
        fetchApi(`/admin/purchases/${id}`, {
          method: "DELETE",
        }),
      confirm: (id: string, data?: { adminNotes?: string }) =>
        fetchApi(`/admin/purchases/${id}/confirm`, {
          method: "POST",
          body: JSON.stringify(data || {}),
        }),
      generateCertificate: (id: string) =>
        fetchApi(`/admin/purchases/${id}/generate-certificate`, {
          method: "POST",
          body: JSON.stringify({}),
        }),
      reject: (id: string, data?: { adminNotes?: string }) =>
        fetchApi(`/admin/purchases/${id}/reject`, {
          method: "POST",
          body: JSON.stringify(data || {}),
        }),
    },
    mailing: {
      listTemplates: (params?: { includeInactive?: boolean }) => {
        const query = new URLSearchParams();
        if (params?.includeInactive) query.set("includeInactive", "true");
        const suffix = query.toString() ? `?${query.toString()}` : "";
        return fetchApi(`/admin/mailing/templates${suffix}`);
      },
      getTemplate: (id: string) => fetchApi(`/admin/mailing/templates/${id}`),
      createTemplate: (data: {
        key?: string;
        name: string;
        description?: string | null;
        category?: string | null;
        subject: string;
        preheader?: string | null;
        body_html?: string | null;
        body_text?: string | null;
        is_active?: boolean;
        is_default?: boolean;
        preview_data_json?: string | null;
      }) =>
        fetchApi("/admin/mailing/templates", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      updateTemplate: (
        id: string,
        data: {
          key?: string;
          name: string;
          description?: string | null;
          category?: string | null;
          subject: string;
          preheader?: string | null;
          body_html?: string | null;
          body_text?: string | null;
          is_active?: boolean;
          is_default?: boolean;
          preview_data_json?: string | null;
        }
      ) =>
        fetchApi(`/admin/mailing/templates/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      disableTemplate: (id: string) =>
        fetchApi(`/admin/mailing/templates/${id}`, {
          method: "DELETE",
        }),
      duplicateTemplate: (id: string) =>
        fetchApi(`/admin/mailing/templates/${id}/duplicate`, {
          method: "POST",
          body: JSON.stringify({}),
        }),
      previewTemplate: (
        id: string,
        data?: {
          subject?: string;
          preheader?: string;
          body_html?: string;
          body_text?: string;
          previewData?: Record<string, string>;
          preview_data_json?: string;
        }
      ) =>
        fetchApi(`/admin/mailing/templates/${id}/preview`, {
          method: "POST",
          body: JSON.stringify(data || {}),
        }),
      previewDraft: (data: {
        subject?: string;
        preheader?: string;
        body_html?: string;
        body_text?: string;
        previewData?: Record<string, string>;
        preview_data_json?: string;
      }) =>
        fetchApi("/admin/mailing/templates/preview", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      listLogs: () => fetchApi("/admin/mailing/logs"),
      sendMailing: (data: {
        templateId: string;
        investorName: string;
        investorEmail: string;
        subject?: string;
        customMessage?: string;
      }) =>
        fetchApi("/admin/mailing/send", {
          method: "POST",
          body: JSON.stringify(data),
        }),
    },
    certifications: {
      issue: (data: { investmentId: string; fileUrl?: string; metadataJson?: string }) =>
        fetchApi("/admin/certifications", {
          method: "POST",
          body: JSON.stringify(data),
        }),
    },
    fx: {
      getUsdToEgp: (date?: string) => {
        const query = new URLSearchParams();
        if (date) query.set("date", date);
        return fetchApi(`/admin/fx/usd-egp?${query}`);
      },
    },
    companies: {
      list: () => fetchApi("/admin/companies"),
      get: (id: string) => fetchApi(`/admin/companies/${id}`),
      create: (data: {
        name: string;
        slug: string;
        status?: "active" | "paused" | "planned" | "closed";
        website_url?: string;
        description?: string;
        country?: string;
        city?: string;
        base_currency?: "USD" | "EGP";
        start_date?: string;
        venture_type?: "internal" | "external";
        foundifai_ownership_percent?: number;
        ownership_notes?: string;
      }) =>
        fetchApi("/admin/companies", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: any) =>
        fetchApi(`/admin/companies/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        fetchApi(`/admin/companies/${id}`, {
          method: "DELETE",
        }),
    },
    ledger: {
      entries: {
        list: (params: { company_id: string; from?: string; to?: string; type?: string; category?: string; is_estimate?: boolean; currency?: string }) => {
          const query = new URLSearchParams();
          query.set("company_id", params.company_id);
          if (params?.from) query.set("from", params.from);
          if (params?.to) query.set("to", params.to);
          if (params?.type) query.set("type", params.type);
          if (params?.category) query.set("category", params.category);
          if (params?.is_estimate !== undefined) query.set("is_estimate", params.is_estimate.toString());
          if (params?.currency) query.set("currency", params.currency);
          return fetchApi(`/admin/ledger/entries?${query}`);
        },
        create: (data: {
          company_id: string;
          entry_date: string;
          type: "revenue" | "expense";
          category: string;
          amount_original: number;
          currency_original?: "USD" | "EGP";
          fx_rate_to_usd?: number;
          fx_source?: string;
          payment_method?: string;
          notes?: string;
          is_estimate?: boolean;
        }) =>
          fetchApi("/admin/ledger/entries", {
            method: "POST",
            body: JSON.stringify(data),
          }),
        update: (id: string, data: Partial<{
          company_id: string;
          entry_date: string;
          type: "revenue" | "expense";
          category: string;
          amount_original: number;
          currency_original: "USD" | "EGP";
          fx_rate_to_usd: number;
          fx_source: string;
          payment_method: string;
          notes: string;
          is_estimate: boolean;
        }>) =>
          fetchApi(`/admin/ledger/entries/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
          }),
        delete: (id: string) =>
          fetchApi(`/admin/ledger/entries/${id}`, {
            method: "DELETE",
          }),
      },
      summary: (params: { company_id: string; from?: string; to?: string; is_estimate?: boolean; currency?: string }) => {
        const query = new URLSearchParams();
        query.set("company_id", params.company_id);
        if (params?.from) query.set("from", params.from);
        if (params?.to) query.set("to", params.to);
        if (params?.is_estimate !== undefined) query.set("is_estimate", params.is_estimate.toString());
        if (params?.currency) query.set("currency", params.currency);
        return fetchApi(`/admin/ledger/summary?${query}`);
      },
      lifetimeSummary: (params: { company_id: string; include_estimates?: boolean }) => {
        const query = new URLSearchParams();
        query.set("company_id", params.company_id);
        if (params?.include_estimates !== undefined) query.set("include_estimates", params.include_estimates.toString());
        return fetchApi(`/admin/ledger/lifetime-summary?${query}`);
      },
      consolidatedSummary: (params: { from?: string; to?: string; include_estimates?: boolean }) => {
        const query = new URLSearchParams();
        if (params?.from) query.set("from", params.from);
        if (params?.to) query.set("to", params.to);
        if (params?.include_estimates !== undefined) query.set("include_estimates", params.include_estimates.toString());
        return fetchApi(`/admin/ledger/consolidated-summary?${query}`);
      },
    },
  },
};
